import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { IAuthService } from '../../../users/services/authService';
import { Result } from '../../../../shared/core/Result';
import { right, left } from '../../../../shared/core/Either';
import { EventItemResponse, EventRequestDTO } from '../infra/types';
import { EventModel } from '../models/Events.model';
import { EventUtil } from '../utils/EventUtils';
import { PaginationDataModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { PaginationUtil } from '../../../../shared/components/pagination/utils/PaginationUtils';
import { AxiosError } from 'axios';

export interface IEventService {
  getEventItems(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
    archived?: boolean,
    deleted?: boolean,
  ): Promise<APIResponse<PaginationDataModel<EventModel[]>>>;
  createEventItem(
    eventItemRequest: EventRequestDTO,
  ): Promise<APIResponse<void>>;
  updateEventItem(
    eventItemRequest: EventRequestDTO,
  ): Promise<APIResponse<void>>;
}

export class EventService extends BaseAPI implements IEventService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.name}`);
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  public async createEventItem(
    eventItemRequest: EventRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post<EventRequestDTO>({
        url: '/v1/base/events',
        requiresToken: true,
        payload: eventItemRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async updateEventItem(
    eventItemRequest: EventRequestDTO,
  ): Promise<APIResponse<void>> {
    if (!eventItemRequest.id)
      return left({ message: 'Event Item was invalid or could not be found' });

    try {
      await this.patch<EventRequestDTO>({
        url: `/v1/base/events/${eventItemRequest.id}`,
        requiresToken: true,
        payload: eventItemRequest,
      });
      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async getEventItems(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
    archived?: boolean,
    deleted?: boolean,
  ): Promise<APIResponse<PaginationDataModel<EventModel[]>>> {
    try {
      const response = await this.get<EventItemResponse>({
        url: '/v1/base/events',
        requiresToken: true,
        config: { params: { offset, filter, dir, search, archived, deleted } },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<EventModel[]>>({
          meta: PaginationUtil.mapToViewModel(meta),
          model: data.map((event) => EventUtil.mapToViewModel(event)),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
}
